import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, makeStyles } from '@material-ui/core';

import { useDebounce } from '../../../shared/hooks/use-debounce.hook';
import { DEFAULT_ROWS_PER_PAGE } from '../../../shared/constants/table.constants';
import { MIN_SEARCH_LENGTH } from '../../../shared/constants/search.constants';
import {
  ArchivableStatusFilter,
  toArchivableListFilters,
} from '../../../shared/constants/entity-status-filter.constants';
import { useAdminGetDeliveryZones } from '../../hooks/admin-get-delivery-zones.hook';
import { useCreateDeliveryZone } from '../../hooks/create-delivery-zone.hook';
import { ADMIN_DELIVERY_ZONES_APP_PATHS } from '../../constants/delivery-zones-app-paths.constants';
import { DeliveryZoneSortColumn, DeliveryZoneSortDirection } from '../../types/delivery-zone.type';
import { CreateDeliveryZoneRequestDTO } from '../../types/dtos/create-delivery-zone.dto';
import { DeliveryZonesListFilters } from '../../components/Containers/DeliveryZonesListFilters.component';
import { DeliveryZonesTable } from '../../components/Tables/DeliveryZonesTable.component';
import { CreateDeliveryZoneDialog } from '../../components/Modals/CreateDeliveryZoneDialog.component';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '0.7rem',

    '& > h2': {
      margin: 0,
    },
  },

  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },

  createButton: {
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export default function AdminDeliveryZonesListView() {
  const classes = useStyles();
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ArchivableStatusFilter>('active');
  const [sortBy, setSortBy] = useState<DeliveryZoneSortColumn>('name');
  const [sortDirection, setSortDirection] = useState<DeliveryZoneSortDirection>('asc');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const debouncedSearch = useDebounce(search, 400).trim();

  const { isLoading, isRefetching, totalCount, deliveryZones } = useAdminGetDeliveryZones({
    page: page + 1,
    limit: rowsPerPage,
    sortBy,
    sortDirection,
    filters: {
      ...(debouncedSearch.length >= MIN_SEARCH_LENGTH ? { search: debouncedSearch } : {}),
      ...toArchivableListFilters(statusFilter),
    },
  });
  const { createDeliveryZone, isSubmitting: isCreating } = useCreateDeliveryZone();

  const withPageReset =
    <Value,>(setValue: (value: Value) => void) =>
    (value: Value) => {
      setValue(value);
      setPage(0);
    };

  const handleSortChange = (column: DeliveryZoneSortColumn) => {
    setSortDirection(column === sortBy && sortDirection === 'asc' ? 'desc' : 'asc');
    setSortBy(column);
    setPage(0);
  };

  const handleCreateDeliveryZone = async (data: CreateDeliveryZoneRequestDTO) => {
    const createdDeliveryZone = await createDeliveryZone(data);
    if (!createdDeliveryZone) return;

    setIsCreateDialogOpen(false);
    history.push(ADMIN_DELIVERY_ZONES_APP_PATHS.DELIVERY_ZONE_DETAILS_PATH(createdDeliveryZone.id));
  };

  return (
    <section className={classes.root}>
      <h2>Zones de livraison</h2>
      <div className={classes.toolbar}>
        <DeliveryZonesListFilters
          search={search}
          statusFilter={statusFilter}
          onSearchChange={withPageReset(setSearch)}
          onStatusFilterChange={withPageReset(setStatusFilter)}
        />
        <Button
          size="small"
          variant="contained"
          className={classes.createButton}
          onClick={() => setIsCreateDialogOpen(true)}
        >
          Créer une zone de livraison
        </Button>
      </div>
      <DeliveryZonesTable
        deliveryZones={deliveryZones}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        isLoading={isLoading || isRefetching}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onPageChange={setPage}
        onRowsPerPageChange={withPageReset(setRowsPerPage)}
        onSortChange={handleSortChange}
      />
      <CreateDeliveryZoneDialog
        open={isCreateDialogOpen}
        isSubmitting={isCreating}
        onSubmit={handleCreateDeliveryZone}
        onClose={() => setIsCreateDialogOpen(false)}
      />
    </section>
  );
}
